import React from 'react';

/**
 * The shell's icon set, as inline SVG.
 *
 * No icon font: the image is baked into a Docker image served from a
 * cluster, and a stylesheet pulled from a CDN at runtime is one more thing
 * that can fail on a customer's network. Stroke icons on `currentColor`
 * follow whatever colour the surrounding text has, so the same glyph works
 * on the brand gradient and inside a light or dark menu.
 */
export interface IconProps {
  className?: string;
}

const svgProps = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
};

const make = (name: string, body: React.ReactNode): React.FC<IconProps> => {
  const Icon: React.FC<IconProps> = ({ className }) => (
    <svg {...svgProps} className={className}>
      {body}
    </svg>
  );
  Icon.displayName = `${name}Icon`;
  return Icon;
};

export const DashboardIcon = make(
  'Dashboard',
  <>
    <rect x="3" y="3" width="7" height="9" rx="1.5" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" />
  </>,
);

export const DevicesIcon = make(
  'Devices',
  <>
    <rect x="4" y="4" width="16" height="16" rx="2" />
    <rect x="9" y="9" width="6" height="6" rx="1" />
    <path d="M9 1v3M15 1v3M9 20v3M15 20v3M1 9h3M1 15h3M20 9h3M20 15h3" />
  </>,
);

export const ClientsIcon = make(
  'Clients',
  <>
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </>,
);

export const TemplatesIcon = make(
  'Templates',
  <>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" />
  </>,
);

export const DeviceTypesIcon = make(
  'DeviceTypes',
  <>
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
    <circle cx="7" cy="7" r="1.5" />
  </>,
);

export const OtaIcon = make(
  'Ota',
  <>
    <path d="M16 16l-4-4-4 4M12 12v9" />
    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
  </>,
);

export const UsersIcon = make(
  'Users',
  <>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="10" cy="7" r="4" />
    <path d="M16 11l2 2 4-4" />
  </>,
);

export const AuditIcon = make(
  'Audit',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </>,
);

export const SettingsIcon = make(
  'Settings',
  <>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    <circle cx="12" cy="12" r="3" />
  </>,
);

export const PortabilityIcon = make(
  'Portability',
  <>
    <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
    <path d="M15 3h6v6" />
    <path d="M21 3l-7 7" />
  </>,
);

export const SunIcon = make(
  'Sun',
  <>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </>,
);

export const MoonIcon = make('Moon', <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />);

export const MenuIcon = make('Menu', <path d="M3 6h18M3 12h18M3 18h18" />);

export const ChevronsLeftIcon = make('ChevronsLeft', <path d="M11 17l-5-5 5-5M18 17l-5-5 5-5" />);

export const ChevronsRightIcon = make('ChevronsRight', <path d="M13 17l5-5-5-5M6 17l5-5-5-5" />);

export const ChevronDownIcon = make('ChevronDown', <path d="M6 9l6 6 6-6" />);

/** A biscuit with a bite taken: the cookie settings link. */
export const CookieIcon = make(
  'Cookie',
  <>
    <path d="M12 2a10 10 0 1 0 10 10 4 4 0 0 1-4-4 4 4 0 0 1-4-4 1 1 0 0 1-2-2z" />
    <circle cx="8.5" cy="10.5" r="1" />
    <circle cx="12" cy="15.5" r="1" />
    <circle cx="16" cy="13" r="1" />
  </>,
);

export const InfoIcon = make(
  'Info',
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5M12 8h.01" />
  </>,
);

export const CodeIcon = make('Code', <path d="M8 7l-5 5 5 5M16 7l5 5-5 5" />);

export const ActivityIcon = make('Activity', <path d="M22 12h-4l-3 9L9 3l-3 9H2" />);

export const BellIcon = make(
  'Bell',
  <>
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </>,
);

export const TerminalIcon = make(
  'Terminal',
  <>
    <path d="M4 17l6-6-6-6" />
    <path d="M12 19h8" />
  </>,
);

export const LogOutIcon = make(
  'LogOut',
  <>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </>,
);

export const SearchIcon = make('Search', <><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></>);

export const CloseIcon = make('Close', <path d="M18 6L6 18M6 6l12 12" />);
